'use client';

import { useEffect } from 'react';

const SMART_CROP_SELECTOR = 'img[data-smartcrop="true"]:not([data-smartcrop-applied="true"])';
const DISABLED_CONTAINER_SELECTOR = '[data-smartcrop-disabled="true"]';

const isScene7Image = (node) => {
  const src = node.getAttribute('data-src') || node.getAttribute('src') || '';

  try {
    const { hostname, pathname } = new URL(src, window.location.href);
    return hostname.endsWith('.scene7.com') && pathname.startsWith('/is/image/');
  } catch {
    return false;
  }
};

const waitForS7 = () =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 3;
    const interval = 3000;

    const check = () => {
      if (typeof window.s7responsiveImage === 'function') {
        resolve();
      } else if (++attempts < maxAttempts) {
        window.setTimeout(check, interval);
      } else {
        reject(new Error('s7responsiveImage not available after 3 attempts'));
      }
    };

    check();
  });

const collectSmartCropNodes = (root) => {
  if (!(root instanceof Element)) {
    return [];
  }

  const nodes = root.matches(SMART_CROP_SELECTOR) ? [root] : [];
  return [...nodes, ...root.querySelectorAll(SMART_CROP_SELECTOR)];
};

const isSmartCropDisabled = (node) =>
  node instanceof Element && Boolean(node.closest(DISABLED_CONTAINER_SELECTOR));

export default function SmartCropEnhancer() {
  useEffect(() => {
    const isEnabled = !['false', '0', 'off', 'no'].includes(
      String(process.env.NEXT_PUBLIC_DSM_SMARTCROP_ENABLED || '')
        .trim()
        .toLowerCase(),
    );

    if (!isEnabled) {
      return undefined;
    }

    let isActive = true;

    const applySmartCrop = async (nodes = collectSmartCropNodes(document.body)) => {
      if (!nodes.length) {
        return;
      }

      try {
        await waitForS7();

        if (!isActive || typeof window.s7responsiveImage !== 'function') {
          return;
        }

        nodes.forEach((node) => {
          if (!(node instanceof HTMLImageElement)) {
            return;
          }

          if (isSmartCropDisabled(node) || !isScene7Image(node)) {
            return;
          }

          node.dataset.mode = 'smartcrop';

          if (node.dataset.smartcropApplied === 'true') {
            return;
          }

          window.s7responsiveImage(node);
          node.dataset.smartcropApplied = 'true';
        });
      } catch (error) {
        console.warn(error.message);
      }
    };

    const onLoad = () => {
      applySmartCrop();
    };

    if (document.readyState === 'complete') {
      applySmartCrop();
    } else {
      window.addEventListener('load', onLoad);
    }

    const observer =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver((mutations) => {
            const pendingNodes = mutations.flatMap((mutation) =>
              [...mutation.addedNodes].flatMap((node) => collectSmartCropNodes(node)),
            );

            if (pendingNodes.length) {
              applySmartCrop(pendingNodes);
            }
          });

    observer?.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      isActive = false;
      window.removeEventListener('load', onLoad);
      observer?.disconnect();
    };
  }, []);

  return null;
}
